import moment from "moment";
import dayjs from "dayjs";
import { toFixed } from "@shared/utils/numbers";
import {
  DAYS_IN_YEAR,
  HOURS_IN_DAY,
  LIFETIME_DATE,
  MINUTES_IN_HOUR,
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
  SECONDS_IN_MONTH,
  SECONDS_IN_WEEK,
  SECONDS_IN_YEAR,
  TIME_RANGE_SUFFIX,
  TIME_UNIT_TO_MULTIPLIER,
  TIME_UNITS_ORDER,
} from "@shared/common/constants";
import { TimeUnit } from "@shared/types";

export const dateToMoment = (date, isUnix) => {
  return isUnix ? moment.unix(date) : moment.utc(date);
};

export const dateToDayjs = (date, isUnix) => {
  return isUnix ? dayjs.unix(date) : dayjs.utc(date);
};

export const getDateFormatString = (
  includeTime = false,
  isShort = false,
  includeYear = false
) => {
  const baseFormat = isShort
    ? `DD MMM${includeYear ? ", YY" : ""}${includeTime ? ", " : ""}`
    : "MMM Do, YY";
  return `${baseFormat}${includeTime ? " HH:mm" : ""}`;
};

export const formatMoment = (momentDate, includeTime, isShort = false) => {
  const format = getDateFormatString(includeTime, isShort);
  return momentDate.format(format);
};

export const formatDate = (
  date,
  includeTime,
  isUnix,
  isShort = false,
  includeYear = false
) => {
  const momentDate = dateToMoment(date, isUnix);
  const format = getDateFormatString(includeTime, isShort, includeYear);
  return momentDate.locale("en").format(format);
};

export const getFormatTime = (date, isUnix) => {
  return dateToMoment(date, isUnix).locale("en").format("HH:mm");
};

export const formatDateRelative = (date, isUnix, isShort, showAgo) => {
  if (isShort) {
    moment.updateLocale("en", {
      relativeTime: {
        future: "in %s",
        past: `%s${showAgo ? " ago" : ""}`,
        s: "1s",
        ss: "%ds",
        m: "1m",
        mm: "%dm",
        h: "1h",
        hh: "%dh",
        d: "1d",
        dd: "%dd",
        M: "1M",
        MM: "%dM",
        y: "1y",
        yy: "%dy",
      },
    });
  }
  if (date) return dateToMoment(date, isUnix).fromNow();
  else return "N/A";
};

export const getDayjsLifetimeDate = () => {
  return dayjs(LIFETIME_DATE, "MM-DD-YYYY");
};

export const getLifetimeDate = () => {
  return moment.utc(LIFETIME_DATE, "MM-DD-YYYY");
};

export const dateIsLifetime = (date, isUnix) => {
  return (
    dateToMoment(date, isUnix).diff(getLifetimeDate(), "years") === 0 ||
    (isUnix && date > 20000000000)
  );
};

export const sortDateStrings = (a, b) => {
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }
  return moment(a).diff(moment(b));
};

export const getLiveTradeTimeDuration = (initialTimeStamp, liveGraphData) => {
  let duration;
  let timeRange;
  if (liveGraphData && liveGraphData.length > 0) {
    const difference =
      liveGraphData[liveGraphData?.length - 1][0] - initialTimeStamp;

    if (difference <= SECONDS_IN_HOUR) {
      duration = difference / SECONDS_IN_MINUTE;
      if (duration < 1) return "<1m";
      timeRange = TIME_RANGE_SUFFIX.MINUTE;
    } else if (difference <= SECONDS_IN_DAY) {
      duration = difference / SECONDS_IN_HOUR;
      timeRange = TIME_RANGE_SUFFIX.HOUR;
    } else if (difference <= SECONDS_IN_WEEK) {
      duration = difference / SECONDS_IN_DAY;
      timeRange = TIME_RANGE_SUFFIX.DAY;
    } else if (difference <= SECONDS_IN_MONTH) {
      duration = difference / SECONDS_IN_WEEK;
      timeRange = TIME_RANGE_SUFFIX.WEEK;
    } else if (difference <= SECONDS_IN_YEAR) {
      duration = difference / SECONDS_IN_MONTH;
      timeRange = TIME_RANGE_SUFFIX.MONTH;
    } else {
      duration = difference / SECONDS_IN_YEAR;
      timeRange = TIME_RANGE_SUFFIX.YEAR;
    }
    return `${toFixed(duration, 0)}${timeRange}`;
  }
};

export const getDaysFromDate = (date, isUnix) => {
  return Math.floor(
    moment.duration(moment().diff(dateToMoment(date, isUnix))).asDays()
  );
};

export const getUtcDate = (year, month, day, hours, minutes, seconds) =>
  new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds)).getTime();

const getTime = (time) => Math.max(0, Math.floor(time));

export const getTimeLeft = (countdown) => {
  const days = getTime(countdown / 1000 / 60 / 60 / 24);
  const hours = getTime((countdown / 1000 / 60 / 60) % 24);
  const minutes = getTime((countdown / 1000 / 60) % 60);
  const seconds = getTime((countdown / 1000) % 60);
  return { days, hours, minutes, seconds };
};

export const getDisplayedTime = (number) =>
  number > 9 ? number : "0" + number;

export const getDisplayTime = (timeUnit, time) => {
  const displayTime = Math.floor(time);
  let displayText = `${displayTime} ${timeUnit}${displayTime > 1 ? "s" : ""}`;
  const diff = time - displayTime;
  const index = TIME_UNITS_ORDER.findIndex((time) => time === timeUnit);
  if (diff > 0.05 && index < TIME_UNITS_ORDER?.length - 1) {
    const smallerTimeUnit = TIME_UNITS_ORDER[index + 1];
    const smallerTimeUnitValue = Math.floor(
      diff * TIME_UNIT_TO_MULTIPLIER[timeUnit]
    );
    displayText = displayText.concat(
      " ",
      smallerTimeUnitValue.toString(),
      ` ${smallerTimeUnit}${smallerTimeUnitValue > 1 ? "s" : ""}`
    );
  }
  return displayText;
};

export const getDisplayTimeDuration = (seconds) => {
  if (!seconds) return;
  let displayText;
  const minutes = seconds / SECONDS_IN_MINUTE;
  const hours = minutes / MINUTES_IN_HOUR;
  const days = hours / HOURS_IN_DAY;
  const years = days / DAYS_IN_YEAR;
  if (years >= 1) {
    displayText = getDisplayTime(TimeUnit.YEAR, years);
  } else if (days >= 1) {
    displayText = getDisplayTime(TimeUnit.DAY, days);
  } else if (hours >= 1) {
    displayText = getDisplayTime(TimeUnit.HOUR, hours);
  } else if (minutes >= 1) {
    displayText = getDisplayTime(TimeUnit.MINUTE, minutes);
  } else {
    displayText = getDisplayTime(TimeUnit.SECOND, seconds);
  }
  return displayText;
};

export const getDaysDiff = (start, end) => {
  return Math.round((end - start) / (60 * 60 * 24));
};
